import React, { FC, useEffect } from 'react';
import {
  Control,
  UseFormClearErrors,
  UseFormSetError,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';

import { HelpIconVariant } from '../../../../../assets/icons/help-icon';
import { HelpIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '../../../../../components/hover-card/index';
import { FormInput } from '../../../../../components/input/index';
import { useFormat } from '../../../../../hooks/index';
import { ReverseVestingTerminationPreviewRow } from '../../../../../hooks/stakeholder/index';
import { StringKey } from '../../../../../lang/index';
import { selectedCompanyStorage } from '../../../../../storage/company/index';
import { formatDisplayValue } from '../../../../../utils/format-display-value.util';
import { fromBackendDateFormat } from '../../../../../utils/from-backend-date-format.util';
import { getTranslation } from '../../../../../utils/get-translation.util';
import {
  isAcceleratedShareCountOverCap,
  reverseVestingTerminationRowTotals,
} from '../reverse-vesting-termination.util';
import { FormSchema } from '../terminate-stakeholder-modal.validation';

/**
 * Repurchase prices are frequently par values (0.0001 and the like), so the field has to carry the
 * full 7 decimals the AC calls for — anything shorter rounds a real price to zero.
 */
const REPURCHASE_PRICE_DECIMALS = 7;

/** Compensation is a money amount, so it displays at cents precision regardless of the price's. */
const COMPENSATION_DECIMALS = 2;

/**
 * Column widths, shared by the header and the body so the two can never drift. The two operator
 * inputs are the widest of the narrow columns because each carries a 40px field.
 */
const columnWidth = {
  accelerated: 'w-[140px]',
  compensation: 'w-[130px]',
  contractualUnvested: 'w-[158px]',
  contractualVested: 'w-[142px]',
  finalVested: 'w-[112px]',
  fullVesting: 'w-[122px]',
  issueDate: 'w-[122px]',
  issued: 'w-[92px]',
  lapsed: 'w-[92px]',
  repurchasePrice: 'w-[152px]',
};

const headerCellClassName = 'flex grow items-center gap-2 px-4 py-3';
const headerLabelClassName = 'font-medium text-[#172335] whitespace-nowrap text-label-md';
const bodyCellClassName = 'flex grow items-center px-4 py-3';
const bodyValueClassName = 'whitespace-nowrap text-sm font-[450] text-gray-700';

type ReverseVestingColumnHeader = {
  key: string;
  labelId: StringKey;
  tooltipId?: StringKey;
  widthClassName: string;
};

const columnHeaders: ReverseVestingColumnHeader[] = [
  { key: 'issue-date', labelId: 'ISSUE_DATE', widthClassName: columnWidth.issueDate },
  { key: 'full-vesting', labelId: 'FULL_VESTING_LABEL', widthClassName: columnWidth.fullVesting },
  { key: 'issued', labelId: 'ISSUED', widthClassName: columnWidth.issued },
  {
    key: 'contractual-vested',
    labelId: 'VESTED_CONTRACTUAL',
    widthClassName: columnWidth.contractualVested,
  },
  {
    key: 'contractual-unvested',
    labelId: 'UNVESTED_CONTRACTUAL',
    widthClassName: columnWidth.contractualUnvested,
  },
  {
    key: 'accelerated',
    labelId: 'ACCELERATED',
    tooltipId: 'ACCELERATED_TOOLTIP',
    widthClassName: columnWidth.accelerated,
  },
  { key: 'final-vested', labelId: 'FINAL_VESTED', widthClassName: columnWidth.finalVested },
  { key: 'lapsed', labelId: 'LAPSED', widthClassName: columnWidth.lapsed },
  {
    key: 'repurchase-price',
    labelId: 'REPURCHASE_PRICE',
    tooltipId: 'REPURCHASE_PRICE_TOOLTIP',
    widthClassName: columnWidth.repurchasePrice,
  },
  { key: 'compensation', labelId: 'COMPENSATION', widthClassName: columnWidth.compensation },
];

export type ReverseVestingTableProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  control: Control<FormSchema>;
  isPending?: boolean;
  rows: ReverseVestingTerminationPreviewRow[];
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  stakeholderIndex: number;
};

/**
 * One row per reverse-vesting issuance the stakeholder holds, each calculated independently — a
 * founder whose reverse-vested shares came from two events settles them at two different
 * schedules, so they can never be summed into a single row.
 *
 * Styling mirrors the Grants table this section sits under: the two live side by side inside the
 * termination modal and have to read as one table repeated, not as two.
 *
 * The contractual columns come from the backend projection and re-arrive whenever the
 * stakeholder-level Last Vesting Date changes; Accelerated and Rep. Price are the operator's, and
 * survive that refresh.
 */
export const ReverseVestingTable: FC<ReverseVestingTableProps> = ({
  clearErrors,
  control,
  isPending,
  rows,
  setError,
  setFormData,
  stakeholderIndex,
}) => (
  <div className="h-fit w-full rounded-lg border border-gray-100 bg-white p-1">
    <table className="w-full">
      <thead>
        <tr className="flex w-full bg-gray-50">
          {columnHeaders.map(({ key, labelId, tooltipId, widthClassName }) => (
            <th className={`${headerCellClassName} ${widthClassName}`} key={key}>
              <span className={headerLabelClassName}>
                <AppFormattedMessage id={labelId} />
              </span>
              {tooltipId && (
                <HoverCard>
                  <HoverCardTrigger>
                    <HelpIcon variant={HelpIconVariant.EXERCISE} />
                  </HoverCardTrigger>
                  <HoverCardContent
                    className="w-max rounded-lg bg-[#101828] px-3 py-2"
                    isShownTriangle
                    side="bottom"
                    triangleClassName="top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ml-0"
                  >
                    <span className="text-label-md font-[550] text-white">
                      <AppFormattedMessage id={tooltipId} />
                    </span>
                  </HoverCardContent>
                </HoverCard>
              )}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y-[1px] divide-gray-100">
        {rows.map((row, index) => (
          <ReverseVestingRow
            clearErrors={clearErrors}
            control={control}
            index={index}
            isPending={isPending}
            key={row.id}
            row={row}
            setError={setError}
            setFormData={setFormData}
            stakeholderIndex={stakeholderIndex}
          />
        ))}
      </tbody>
    </table>
  </div>
);

type ReverseVestingRowProps = {
  clearErrors: UseFormClearErrors<FormSchema>;
  control: Control<FormSchema>;
  index: number;
  isPending?: boolean;
  row: ReverseVestingTerminationPreviewRow;
  setError: UseFormSetError<FormSchema>;
  setFormData: UseFormSetValue<FormSchema>;
  stakeholderIndex: number;
};

const ReverseVestingRow: FC<ReverseVestingRowProps> = ({
  clearErrors,
  control,
  index,
  isPending,
  row,
  setError,
  setFormData,
  stakeholderIndex,
}) => {
  const { format } = useFormat();
  const company = selectedCompanyStorage(({ company }) => company);

  const currency = company?.currency?.symbol || company?.currency?.iso3.toUpperCase();
  const basePath =
    `terminateForm.stakeholderItems.${stakeholderIndex}.reverseVestingItems.${index}` as const;

  // Watch scoped to this row only — a whole-form watch re-renders every row of every table in the
  // modal on each keystroke anywhere in it.
  const formRow = useWatch({ control, name: basePath }) as
    | NonNullable<
        FormSchema['terminateForm']['stakeholderItems'][number]['reverseVestingItems']
      >[number]
    | undefined;
  const acceleratedShares = Number(formRow?.acceleratedShares ?? 0);
  const repurchasePrice = Number(formRow?.repurchasePrice ?? row.defaultRepurchasePrice);

  // Seeds the row and re-seeds the contractual figures the projection owns. Accelerated and
  // Rep. Price are only defaulted when absent, so a refresh triggered by a Last Vesting Date edit
  // never discards what the operator typed.
  useEffect(() => {
    setFormData(`${basePath}.id`, row.id);
    setFormData(`${basePath}.contractualVestedShares`, row.contractualVestedShares);
    setFormData(`${basePath}.contractualUnvestedShares`, row.contractualUnvestedShares);
    setFormData(`${basePath}.reverseVestingShares`, row.reverseVestingShares);
  }, [
    basePath,
    row.contractualUnvestedShares,
    row.contractualVestedShares,
    row.id,
    row.reverseVestingShares,
    setFormData,
  ]);

  useEffect(() => {
    if (formRow?.acceleratedShares === undefined) setFormData(`${basePath}.acceleratedShares`, 0);
    if (formRow?.repurchasePrice === undefined) {
      setFormData(`${basePath}.repurchasePrice`, row.defaultRepurchasePrice);
    }
    // Rule name deliberately omitted: the CRA build runs an eslint config without the react-hooks
    // plugin, so naming `react-hooks/exhaustive-deps` here fails `yarn build` outright.
    // eslint-disable-next-line -- defaults only; re-running on every keystroke would fight the user
  }, [basePath, row.defaultRepurchasePrice, setFormData]);

  // Re-checked on both inputs and on every projection refresh: lowering the Last Vesting Date
  // shrinks the ceiling under an Accelerated value that was valid a moment ago. The message is the
  // ceiling itself rather than a sentence — the column is too narrow for one.
  useEffect(() => {
    const path = `${basePath}.acceleratedShares` as const;

    if (
      isAcceleratedShareCountOverCap({
        acceleratedShares,
        contractualUnvestedShares: row.contractualUnvestedShares,
      })
    ) {
      setError(path, {
        type: 'manual',
        message: getTranslation('MAX_VALUE', {
          value: formatDisplayValue(row.contractualUnvestedShares, { roundTo: 0 }),
        }),
      });
      return;
    }

    clearErrors(path);
  }, [acceleratedShares, basePath, clearErrors, row.contractualUnvestedShares, setError]);

  const { compensation, finalVestedShares, lapsedShares } = reverseVestingTerminationRowTotals({
    acceleratedShares,
    contractualVestedShares: row.contractualVestedShares,
    disposedShares: row.disposedShares,
    repurchasePrice,
    reverseVestingShares: row.reverseVestingShares,
  });

  // Matches the Grants table: a figure still being projected reads as "-" rather than as a stale 0.
  const projected = (value: number) =>
    isPending ? '-' : formatDisplayValue(value, { roundTo: 0 });

  return (
    <tr className="flex w-full bg-white">
      <td className={`${bodyCellClassName} ${columnWidth.issueDate}`}>
        <span className={bodyValueClassName}>
          {format(fromBackendDateFormat(row.issueDate), 'dd/MM/yyyy')}
        </span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.fullVesting}`}>
        <span className={bodyValueClassName}>
          {format(fromBackendDateFormat(row.fullVestingDate), 'dd/MM/yyyy')}
        </span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.issued}`}>
        <span className={bodyValueClassName}>
          {formatDisplayValue(row.reverseVestingShares, { roundTo: 0 })}
        </span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.contractualVested}`}>
        <span className={bodyValueClassName}>{projected(row.contractualVestedShares)}</span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.contractualUnvested}`}>
        <span className={bodyValueClassName}>{projected(row.contractualUnvestedShares)}</span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.accelerated}`}>
        <FormInput
          autoComplete="off"
          control={control}
          inputClassName="h-10"
          name={`${basePath}.acceleratedShares`}
          numberOnly
          shouldDisplayZero
          shouldFormatNumber
          wrapperClassName="w-[108px]"
        />
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.finalVested}`}>
        <span className={bodyValueClassName}>{projected(finalVestedShares)}</span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.lapsed}`}>
        <span className={bodyValueClassName}>{projected(lapsedShares)}</span>
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.repurchasePrice}`}>
        <FormInput
          autoComplete="off"
          control={control}
          inputClassName="h-10"
          maxDecimalPlaces={REPURCHASE_PRICE_DECIMALS}
          name={`${basePath}.repurchasePrice`}
          numberOnly
          shouldDisplayZero
          shouldFormatNumber
          wrapperClassName="w-[120px]"
        />
      </td>
      <td className={`${bodyCellClassName} ${columnWidth.compensation}`}>
        <span className={bodyValueClassName}>
          {isPending
            ? '-'
            : formatDisplayValue(compensation, {
                prefix: currency,
                roundTo: COMPENSATION_DECIMALS,
              })}
        </span>
      </td>
    </tr>
  );
};
